import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type CreateStatementItemParams,
  type IStatementItemRepository,
  type ListUnmatchedStatementItemsParams,
  type ListUnmatchedStatementItemsResult,
} from '@webapp/modules/accounts-payable/business-logic/interfaces/IStatementItemRepository'
import { type StatementItemConstructor } from '@webapp/modules/accounts-payable/entities/StatementItem'
import { type StatementItemApiModel } from '../../api-models/StatementItemApiModel'
import { mapStatementItemApiModel } from '../../mappers/mapStatementItemApiModel'

interface Dependency {
  httpClient: IHttpClient
}

interface ApiPaginationApiModel {
  count?: number
  page_size?: number
  num_pages?: number
  current_page?: number
}

interface ListUnmatchedStatementItemsResponse {
  data: StatementItemApiModel[]
  metadata?: {
    summary?: { total_amount?: number }
    pagination?: ApiPaginationApiModel
  }
}

const BASE_URL = '/api/v3/reconcile/statement-items/'

const mapPagination = (
  pagination: ApiPaginationApiModel | undefined
): RepoPaginationResponse => ({
  count: pagination?.count ?? 0,
  pageSize: pagination?.page_size ?? 10,
  numPages: pagination?.num_pages ?? 1,
  page: pagination?.current_page ?? 1,
})

export class StatementItemRepository implements IStatementItemRepository {
  constructor(private readonly deps: Dependency) {}

  async listUnmatched(
    params: ListUnmatchedStatementItemsParams
  ): Promise<ListUnmatchedStatementItemsResult> {
    try {
      const res =
        await this.deps.httpClient.get<ListUnmatchedStatementItemsResponse>(
          `${BASE_URL}unmatched/`,
          {
            params: {
              creditcard: params.creditcardId,
              order_by: params.orderBy ?? 'date',
              page: params.page ?? 1,
              page_size: params.pageSize ?? 10,
              ...(params.search ? { search: params.search } : {}),
              ...(params.matchCreditcardItemId != null
                ? { match_creditcard_item: params.matchCreditcardItemId }
                : {}),
            },
          }
        )

      return {
        items: res.data.data.map(mapStatementItemApiModel),
        totalAmount: res.data.metadata?.summary?.total_amount ?? null,
        pagination: mapPagination(res.data.metadata?.pagination),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async create(
    params: CreateStatementItemParams
  ): Promise<StatementItemConstructor> {
    try {
      const res = await this.deps.httpClient.post<{
        data: StatementItemApiModel
      }>(BASE_URL, {
        creditcard: params.creditcardId,
        description: params.description,
        date: params.date,
        amount: params.amount,
      })

      return mapStatementItemApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async delete(id: number): Promise<void> {
    try {
      // Trailing slash required (204).
      await this.deps.httpClient.delete(`${BASE_URL}${id}/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
